export const DEAL_REPLACE_TOKEN_PARAM = 'dealReplaceToken';
export const DEAL_REPLACE_ACTION_PARAM = 'dealReplaceAction';
export const CREATE_FORM_PARAM = 'createForm';
const STORAGE_KEY = 'keyflo.dealReplaceConfirm';

const ACTION_COPY = {
  cancel_open_file: {
    title: 'Confirm this cancellation',
    body: 'Please confirm so the new contract can be created.',
    confirm: 'Confirm cancellation',
  },
  wrong_address: {
    title: 'Confirm the wrong address',
    body: 'Confirm which purchase agreement needs the corrected address.',
    confirm: 'Confirm address',
  },
  draft_yes: {
    title: 'Cancellation form',
    body: 'KeyFlo is starting the Notice of Cancellation. Check your email for the unsigned form or a link to finish it.',
    confirm: '',
  },
  draft_no: {
    title: 'No draft needed',
    body: 'KeyFlo recorded that no cancellation draft is needed.',
    confirm: '',
  },
  ready_retry: {
    title: 'Confirm the SkySlope retry',
    body: 'You already chose to retry after making the listing Active. Confirm to convert it to the new sale.',
    confirm: 'Confirm retry',
  },
};

export function dealReplaceParamsFromSearch(search) {
  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(search || '');
  const token = (params.get(DEAL_REPLACE_TOKEN_PARAM) || '').trim();
  const action = (params.get(DEAL_REPLACE_ACTION_PARAM) || '').trim();
  if (!token || !action) {
    return null;
  }
  return { token, action };
}

export function dealReplaceCopy(action, { kind = 'contract', address } = {}) {
  const base = ACTION_COPY[action] || {
    title: 'Confirm this KeyFlo action',
    body: 'Please confirm this KeyFlo action.',
    confirm: 'Confirm',
  };
  if (action === 'cancel_open_file') {
    return {
      ...base,
      body: `Please confirm so the new ${kind === 'listing' ? 'listing' : 'contract'} can be created.`,
    };
  }
  if (action === 'wrong_address') {
    return {
      ...base,
      body: kind === 'listing'
        ? 'Confirm which listing needs the corrected address.'
        : 'Confirm which purchase agreement needs the corrected address.',
    };
  }
  if (action === 'draft_yes') {
    return {
      ...base,
      title: kind === 'listing' ? 'Listing withdrawal' : 'Cancellation form',
      body: kind === 'listing'
        ? 'KeyFlo recorded your request to draft a listing withdrawal.'
        : 'KeyFlo is starting the Notice of Cancellation. Check your email for the unsigned form or a link to finish it.',
    };
  }
  if (action === 'draft_no') {
    return {
      ...base,
      body: kind === 'listing'
        ? 'KeyFlo recorded that no listing withdrawal is needed.'
        : 'KeyFlo recorded that no cancellation draft is needed.',
    };
  }
  return base;
}

function currentPathname(pathname) {
  return pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
}

export function persistDealReplaceParams(search, pathname) {
  const params = dealReplaceParamsFromSearch(search);
  if (!params) {
    return null;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...params,
      path: currentPathname(pathname),
    }));
  } catch {
    // Private mode or quota — the URL still carries the token when present.
  }
  return params;
}

export function readStoredDealReplaceParams() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const token = String(parsed?.token || '').trim();
    const action = String(parsed?.action || '').trim();
    if (!token || !action) {
      return null;
    }
    return {
      token,
      action,
      path: String(parsed?.path || '').trim(),
    };
  } catch {
    return null;
  }
}

export function clearStoredDealReplaceParams() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function searchWithDealReplaceParams(search, pathname) {
  const path = currentPathname(pathname);
  const current = dealReplaceParamsFromSearch(search);
  if (current) {
    persistDealReplaceParams(search, path);
    return { search: typeof search === 'string' ? search : `?${search}`, params: current };
  }
  const stored = readStoredDealReplaceParams();
  if (!stored || (stored.path && path && stored.path !== path)) {
    return { search: typeof search === 'string' ? search : '', params: null };
  }
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  params.set(DEAL_REPLACE_TOKEN_PARAM, stored.token);
  params.set(DEAL_REPLACE_ACTION_PARAM, stored.action);
  return { search: `?${params.toString()}`, params: stored };
}

export function pathWithStoredDealReplace(pathAndSearch) {
  const raw = pathAndSearch || '';
  const qIndex = raw.indexOf('?');
  const path = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const query = qIndex >= 0 ? raw.slice(qIndex) : '';
  const resolved = searchWithDealReplaceParams(query, path);
  return `${path}${resolved.search || ''}`;
}

if (typeof window !== 'undefined' && window.location?.search) {
  persistDealReplaceParams(window.location.search, window.location.pathname);
}

export function namesLine(names) {
  if (Array.isArray(names)) {
    const cleaned = names.map((name) => String(name || '').trim()).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : 'Not listed';
  }
  const text = String(names || '').trim();
  return text || 'Not listed';
}

export function dealReplaceFileBlocks(preview, kind = 'contract') {
  const noun = kind === 'listing' ? 'listing' : 'contract';
  const incomingAddress = preview?.incoming_address_display
    || preview?.incoming_address
    || 'this property';
  const existingAddress = preview?.existing_address || incomingAddress;
  return [
    {
      key: 'existing',
      title: `Existing ${noun}`,
      address: existingAddress,
      buyers: namesLine(preview?.existing_buyers),
      sellers: namesLine(preview?.existing_sellers),
    },
    {
      key: 'incoming',
      title: `New ${noun}`,
      address: incomingAddress,
      buyers: namesLine(preview?.incoming_buyers),
      sellers: namesLine(preview?.incoming_sellers),
    },
  ];
}

export function stripDealReplaceParams(search) {
  const params = new URLSearchParams(search || '');
  params.delete(DEAL_REPLACE_TOKEN_PARAM);
  params.delete(DEAL_REPLACE_ACTION_PARAM);
  params.delete(CREATE_FORM_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function createFormTypeFromSearch(search, action) {
  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(search || '');
  const requested = (params.get(CREATE_FORM_PARAM) || '').trim();
  if (requested) {
    return requested;
  }
  if (action === 'draft_yes') {
    return 'notice_of_cancellation';
  }
  return '';
}
